import { cn } from '~/@/lib/utils'
import { getAvatarColorsForUser } from '../../common/utils/user'
import { ActiveUser } from '../settings/types'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'

type UserAvatarProps = {
  user: Partial<ActiveUser>
  includeTooltip?: boolean
  small?: boolean
  size?: 'sm' | 'md' | 'lg'
  color?: 'gray' | 'auto'
} & React.HTMLAttributes<HTMLDivElement>

export function UserAvatar({
  user,
  includeTooltip,
  small,
  size,
  color = 'auto',
  ...props
}: UserAvatarProps) {
  const effectiveSize = size ?? (small ? 'sm' : 'md')
  const avatarClassnames =
    color === 'auto'
      ? getAvatarColorsForUser(user)
      : `bg-${color}-11 text-${color}-6 border-transparent hover:border-${color}-10`

  const sizeClasses = {
    sm: 'size-6 text-3xs border-transparent',
    md: 'size-8',
    lg: 'size-14 text-lg rounded-xl',
  }[effectiveSize]

  const imgDimension = { sm: 24, md: 32, lg: 56 }[effectiveSize]

  let content = (
    <div
      {...props}
      className={cn(
        'group inline-flex shrink-0 items-center justify-center rounded-full text-2xs border font-normal select-none overflow-hidden',
        sizeClasses,
        props.className,
        avatarClassnames,
      )}
    >
      {user.firstName?.at(0)}
      {user.lastName?.at(0)}
    </div>
  )

  if (user.profilePicture) {
    content = (
      <div
        {...props}
        className={cn(
          'relative shrink-0 rounded-full border overflow-hidden',
          sizeClasses,
          props.className,
        )}
      >
        <div className="absolute inset-0">
          <img
            alt={`${user.firstName} ${user.lastName}`}
            className="size-full"
            height={imgDimension}
            src={user.profilePicture.storageUrl}
            width={imgDimension}
          />
        </div>
      </div>
    )
  }

  if (includeTooltip) {
    return <Tooltip title={`${user.firstName} ${user.lastName}`}>{content}</Tooltip>
  }
  return content
}
